import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';

const SUPPORTED_LANGUAGES = ['es', 'en'] as const;

type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

interface LanguageSwitcherProps {
  reversed?: boolean;
  className?: string;
}

export function LanguageSwitcher({
  reversed = false,
  className,
}: LanguageSwitcherProps): React.JSX.Element {
  const { i18n, t } = useTranslation('common');
  const activeLanguage = i18n.language.startsWith('en') ? 'en' : 'es';

  const handleLanguageChange = (language: SupportedLanguage): void => {
    void i18n.changeLanguage(language);
  };

  return (
    <div
      className={cn(
        'inline-flex gap-1 rounded-full border p-1',
        reversed ? 'border-white/25' : 'border-brand-primary/20',
        className
      )}
      role="group"
      aria-label={t('language.label')}
    >
      {SUPPORTED_LANGUAGES.map((language) => {
        const isActive = activeLanguage === language;

        return (
          <button
            key={language}
            type="button"
            onClick={() => handleLanguageChange(language)}
            aria-pressed={isActive}
            lang={language}
            className={cn(
              'min-h-9 min-w-11 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.1em] transition-colors',
              isActive
                ? reversed
                  ? 'bg-white text-brand-primary'
                  : 'bg-brand-primary text-white'
                : reversed
                  ? 'text-white/75 hover:text-white'
                  : 'text-[var(--ir-muted)] hover:text-brand-primary'
            )}
          >
            {t(`language.options.${language}`)}
          </button>
        );
      })}
    </div>
  );
}
